import { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, Icon } from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import Table from "examples/Tables/Table";
import VuiProgress from "components/VuiProgress";
import { IoIosCheckmarkCircle } from "react-icons/io";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { api } from "../../../../services/api"; // Importando o serviço de API

// Componente para renderizar Nome e Email, espelhando leadsTableData.js
function Name({ name, email }) {
  return (
    <VuiBox display="flex" alignItems="center" px={1} py={0.5}>
      <VuiBox display="flex" flexDirection="column">
        <VuiTypography variant="button" color="white" fontWeight="medium">
          {name}
        </VuiTypography>
        <VuiTypography variant="caption" color="text">
          {email}
        </VuiTypography>
      </VuiBox>
    </VuiBox>
  );
}

function Projects({ isLoading }) {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);

  useEffect(() => {
    if (!isLoading) {
      const fetchLeads = async () => {
        try {
          const data = await api.get(`/leads?page=1&pageSize=6&riskLevel=high`);
          setLeads(data.leads);
        } catch (error) {
          console.error("Erro ao buscar os leads:", error);
          if (error.message.includes("401")) {
            navigate("/authentication/sign-in");
            return;
          }
        }
      };

      fetchLeads();
    }
  }, [isLoading, navigate]);

  const columns = useMemo(() => [
    { name: "nome", align: "left" },
    { name: "telefone", align: "left" },
    { name: "ip", align: "center" },
    { name: "hora de acesso", align: "center" },
    { name: "risco", align: "center" },
    { name: "detalhes", align: "center" },
  ], []);

  const skeletonRows = useMemo(() => [...Array(6)].map((_, i) => ({
    nome: <Skeleton width={150} />,
    telefone: <Skeleton width={100} />,
    ip: <Skeleton width={100} />,
    "hora de acesso": <Skeleton width={120} />,
    risco: <Skeleton width={100} />,
    detalhes: <Skeleton circle width={24} height={24} />,
  })), []);

  const rows = useMemo(() => leads.map(lead => {
    let formData = lead.formData || {};
    if (typeof formData === 'string') {
      try {
        formData = JSON.parse(formData);
      } catch (e) {
        console.error("Erro ao fazer parse do formData:", e);
        formData = {};
      }
    }

    const riskValue = 100; // Para fraudadores, o risco é sempre 100%
    const riskColor = "error";
    const riskLabel = "Fraudador";

    const formattedDate = new Date(lead.createdAt).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

    return {
      nome: <Name name={formData.name || 'N/A'} email={formData.email || 'N/A'} />,
      telefone: (
        <VuiTypography variant="button" color="white" fontWeight="medium">
          {formData.phone || 'N/A'}
        </VuiTypography>
      ),
      ip: (
        <VuiTypography variant="button" color="white" fontWeight="medium">
          {lead.ipAddress}
        </VuiTypography>
      ),
      "hora de acesso": (
        <VuiTypography variant="button" color="white" fontWeight="medium">
          {formattedDate}
        </VuiTypography>
      ),
      risco: (
        <VuiBox sx={{ width: '100px' }}>
          <VuiTypography variant="caption" color="text" sx={{ mb: 0.5 }}>
            {riskLabel}
          </VuiTypography>
          <VuiProgress value={riskValue} color={riskColor} variant="gradient" sx={{ background: "#2D2E5F" }} />
        </VuiBox>
      ),
      detalhes: (
        <Link to={`/dossier/${lead.fraudsterId}`}>
          <Icon style={{ color: "#555B71" }} sx={{ cursor: "pointer", fontWeight: "bold" }} fontSize="small">
            visibility
          </Icon>
        </Link>
      ),
    };
  }), [leads]);

  return (
    <Card sx={{ height: "100% !important" }}>
      <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb="32px">
        <VuiBox mb="auto">
          <VuiTypography color="white" variant="lg" mb="6px" gutterBottom>
            {isLoading ? <Skeleton width={200} /> : "Ultimas Notificações"}
          </VuiTypography>
          <VuiBox display="flex" alignItems="center" lineHeight={0}>
            {isLoading ? <Skeleton width={150} /> : (
              <>
                <IoIosCheckmarkCircle color="red" size="15px" />
                <VuiTypography variant="button" fontWeight="regular" color="text" ml="5px">
                  &nbsp;<strong>{leads.length}</strong> fraudadores recentes
                </VuiTypography>
              </>
            )}
          </VuiBox>
        </VuiBox>
        <VuiButton
          variant="outlined"
          color="white"
          sx={{
            borderColor: "rgba(255, 255, 255, 0.4)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          }}
          onClick={() => navigate('/leads')}
          disabled={isLoading}
        >
          Ver Mais
        </VuiButton>
      </VuiBox>
      <VuiBox
        sx={{
          "& th": {
            borderBottom: ({ borders: { borderWidth }, palette: { grey } }) =>
              `${borderWidth[1]} solid ${grey[700]}`,
          },
          "& .MuiTableRow-root:not(:last-child)": {
            "& td": {
              borderBottom: ({ borders: { borderWidth }, palette: { grey } }) =>
                `${borderWidth[1]} solid ${grey[700]}`,
            },
          },
        }}
      >
        <Table columns={columns} rows={isLoading ? skeletonRows : rows} />
      </VuiBox>
    </Card>
  );
}

export default Projects;
